import { z } from 'zod';

/** `GET /api/bots/:botId/leads` query — cursor is an opaque base64url string (see
 * `packages/db/src/repos/leads-view.ts` `encodeCursor`/`decodeCursor`); an unparseable
 * cursor is treated as "no cursor" by the repo rather than erroring, matching
 * `conversationsQuerySchema`'s rationale (a bad cursor can only be a stale/tampered
 * client, never a client mistake worth rejecting).
 *
 * `status` (triage-state design 2026-08-21 §B) defaults to `all` — unlike
 * `unansweredListQuerySchema`'s `open` default, the Leads list's default view is everything;
 * the dashboard's own default TAB is `new` when there's something new, but that's a client
 * choice, not this schema's. */
export const leadsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  status: z.enum(['new', 'contacted', 'archived', 'all']).default('all'),
});

export type LeadsQuery = z.infer<typeof leadsQuerySchema>;

/** `PATCH /api/bots/:botId/leads/:leadId` body (triage-state design 2026-08-21 §B). Any
 * status is reachable from any other by hand — unlike `unansweredStatusPatchSchema`, nothing
 * here is write-restricted (no equivalent of `answered`). */
export const leadStatusPatchSchema = z.object({
  status: z.enum(['new', 'contacted', 'archived']),
});

export type LeadStatusPatchInput = z.infer<typeof leadStatusPatchSchema>;

/** `POST /api/bots/:botId/leads/bulk-status` body (triage-state design 2026-08-21 §B) —
 * `ids` is capped at 200 per the spec's bulk-action ceiling; `min(1)` rejects an empty
 * selection as a 400 rather than a silent no-op `{ updated: 0 }`. */
export const leadBulkStatusSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(200),
  status: z.enum(['new', 'contacted', 'archived']),
});

export type LeadBulkStatusInput = z.infer<typeof leadBulkStatusSchema>;
